import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
  Pagination,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Analytics,
  TrendingUp,
  People,
  Assessment,
  Refresh,
  Visibility,
  Download,
  Search as SearchIcon,
  Settings as SettingsIcon,
  ArrowBack,
  ArrowForward,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  DeleteOutline as DeleteIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useStore from "../../store";
import { useIntl, FormattedMessage } from "react-intl";
import api from "../../api";
import PillarsDefEditor from "./PillarsDefEditor";
import ProjectPillarsPicker from "./ProjectPillarsPicker";
import PillarTaggingReviewDialog from "./PillarTaggingReviewDialog";
import { clearDraft as clearPillarsDraft, initialRows as initialPillarRows, saveDraft as savePillarsDraft, toPayload as pillarsToPayload, validate as validatePillars } from "./pillarsDef";
import insightsApi from "../../api/insightsApi";
import fileDownload from "js-file-download";
import { getDefaultPillars } from "../../utils/pillarStorage";

// Report-generation status polling (see GeneratingReportCard below).
const REPORT_STATUS_POLL_MS = 7000;            // poll cadence (~7s)
const REPORT_STATUS_TIMEOUT_MS = 40 * 60 * 1000; // give up after ~40 min
// Fallback step labels; the BE may override these via `step_labels`.
const REPORT_STEP_LABELS = [
  "Queued",
  "Tagging pillars",
  "Waiting for your confirmation",
  "Finding topics",
  "Generating report",
  "Scoring risk signals",
  "Done",
];

const GENERATING_STORAGE_KEY = "prompta.generatingInsights";

// Rehydrate generating cards from localStorage, dropping anything older than
// the poll timeout (stale). We store only {startedAt, surveyName}; live state
// (step/state) is re-derived by the polling effect.
function loadPersistedGenerating() {
  try {
    const stored = JSON.parse(localStorage.getItem(GENERATING_STORAGE_KEY) || "{}");
    const now = Date.now();
    const out = {};
    Object.entries(stored).forEach(([id, v]) => {
      const parked = v?.state === "awaiting_confirmation";
      if (v && typeof v.startedAt === "number"
          && (parked || now - v.startedAt < REPORT_STATUS_TIMEOUT_MS)) {
        out[id] = {
          startedAt: v.startedAt,
          state: v.state || "running",
          step: parked ? 3 : 1,
          configId: v.configId ?? null,
          stepLabels: REPORT_STEP_LABELS,
          error: null,
          timedOut: false,
          surveyName: v.surveyName || null,
        };
      }
    });
    return out;
  } catch {
    return {};
  }
}

function persistGenerating(map) {
  try {
    const slim = {};
    Object.entries(map).forEach(([id, v]) => {
      // state/configId are persisted so a parked run survives a refresh: without
    // them the entry rehydrates as "running" and the 40-min age check drops it.
    slim[id] = {
      startedAt: v.startedAt,
      surveyName: v.surveyName || null,
      state: v.state === "awaiting_confirmation" ? v.state : undefined,
      configId: v.configId ?? undefined,
    };
    });
    localStorage.setItem(GENERATING_STORAGE_KEY, JSON.stringify(slim));
  } catch {
    /* ignore quota/serialization errors */
  }
}

// A placeholder card shown at the top of the reports grid while a report is
// being generated. Renders one row per step with done (✓) / current (◐) /
// pending (○) indicators driven by the BE status endpoint — no ETA, no %.
function GeneratingReportCard({ entry, onDismiss, onReview }) {
  const rawLabels = Array.isArray(entry.stepLabels) && entry.stepLabels.length
    ? entry.stepLabels
    : REPORT_STEP_LABELS;
  // BE returns step_labels as [{step, label}] objects; the fallback is plain
  // strings. Normalize to strings so we never render an object as a React child.
  const labels = rawLabels.map((l) => (typeof l === "string" ? l : l?.label ?? ""));
  const currentStep = entry.step || 1;
  const isError = entry.state === "errored";
  const isTimedOut = entry.timedOut;
  // Parked on purpose: the pillar stage finished and nothing advances until the
  // user confirms the tagging. Showing a spinner here would be a lie, and the
  // ~40min timeout must not apply to a run that is waiting for a human.
  const isAwaiting = entry.state === "awaiting_confirmation";
  const settled = isError || isTimedOut || isAwaiting;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px dashed",
        borderColor: isError ? "error.main" : "primary.light",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          {!settled && <CircularProgress size={18} />}
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {entry.surveyName ? (
              <FormattedMessage
                id="insight.generating_report_for"
                defaultMessage="Generating: {name}"
                values={{ name: entry.surveyName }}
              />
            ) : (
              <FormattedMessage id="insight.generating_report" defaultMessage="Generating report…" />
            )}
          </Typography>
        </Box>

        {isError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {entry.error || (
              <FormattedMessage id="insight.generation_failed" defaultMessage="Report generation failed." />
            )}
          </Alert>
        ) : isTimedOut ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <FormattedMessage
              id="insight.generation_still_processing"
              defaultMessage="Still processing — check back later."
            />
          </Alert>
        ) : null}

        <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0 }}>
          {labels.map((label, idx) => {
            const stepNum = idx + 1;
            let stepState;
            if (stepNum < currentStep) {
              stepState = "done";
            } else if (stepNum === currentStep) {
              stepState = isError ? "error" : "current";
            } else {
              stepState = "pending";
            }
            const icon = stepState === "done" ? "✓"
              : stepState === "current" ? "◐"
              : stepState === "error" ? "✕"
              : "○";
            const color = stepState === "done" ? "success.main"
              : stepState === "current" ? "primary.main"
              : stepState === "error" ? "error.main"
              : "text.disabled";
            return (
              <Box
                component="li"
                key={stepNum}
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
              >
                <Typography
                  component="span"
                  sx={{ width: 20, textAlign: "center", color, fontWeight: "bold", lineHeight: 1 }}
                >
                  {icon}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: stepState === "pending" ? "text.secondary" : "text.primary",
                    fontWeight: stepState === "current" ? 600 : 400,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
      {settled && (
        <CardActions sx={{ justifyContent: "flex-end" }}>
          <Button size="small" onClick={onDismiss}>
            <FormattedMessage id="insight.dismiss" defaultMessage="Dismiss" />
          </Button>
          {isAwaiting && (
            <Button size="small" variant="contained" onClick={onReview}>
              <FormattedMessage
                id="insight.review_pillars"
                defaultMessage="Review pillars"
              />
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
}

export default function InsightPage() {
  const intl = useIntl();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  // Insights currently generating a report/config, keyed by insight_id. Each
  // entry: { startedAt, state, step, stepLabels, error, timedOut, surveyName }.
  const [generatingInsights, setGeneratingInsights] = useState(loadPersistedGenerating);
  // Mirror of the above so the polling loop always reads the freshest entries
  // (step/error updates) without tearing down and re-creating the interval.
  const generatingInsightsRef = useRef(generatingInsights);
  useEffect(() => { generatingInsightsRef.current = generatingInsights; }, [generatingInsights]);
  // Persist the generating set so a page refresh mid-generation keeps the card
  // (the backend job keeps running); the poll effect then re-derives live state.
  useEffect(() => { persistGenerating(generatingInsights); }, [generatingInsights]);
  const [alert, setAlert] = useState(null);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [surveysForModal, setSurveysForModal] = useState([]);
  const [surveyModalLoading, setSurveyModalLoading] = useState(false);
  const [surveyModalPage, setSurveyModalPage] = useState(1);
  const [surveyModalTotalPages, setSurveyModalTotalPages] = useState(0);
  const [surveyModalTotalItems, setSurveyModalTotalItems] = useState(0);
  const [surveyModalSearch, setSurveyModalSearch] = useState("");
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  // Generate Config Modal state
  const [generateConfigModalOpen, setGenerateConfigModalOpen] = useState(false);
  const [generateConfigSurveys, setGenerateConfigSurveys] = useState([]);
  const [generateConfigLoading, setGenerateConfigLoading] = useState(false);
  const [generateConfigPage, setGenerateConfigPage] = useState(1);
  const [generateConfigTotalPages, setGenerateConfigTotalPages] = useState(0);
  const [generateConfigTotalItems, setGenerateConfigTotalItems] = useState(0);
  const [generateConfigSearch, setGenerateConfigSearch] = useState("");
  const [selectedConfigSurveyId, setSelectedConfigSurveyId] = useState(null);
  const [triggeringAutoconfig, setTriggeringAutoconfig] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [configViewTab, setConfigViewTab] = useState("topics");
  const [reportName, setReportName] = useState("");
  const [configName, setConfigName] = useState("");
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [editingConfigName, setEditingConfigName] = useState("");
  const [renamingConfig, setRenamingConfig] = useState({});
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingReportName, setEditingReportName] = useState("");
  const [renamingReport, setRenamingReport] = useState({});
  // Insight removal (from the Generate Full Report modal's list). Deleting an
  // insight takes its reports, configs, statuses and risk rows with it, so it
  // always goes through the confirm dialog.
  const [insightToDelete, setInsightToDelete] = useState(null);
  const [deletingInsight, setDeletingInsight] = useState({});

  // JSON import (Generate Full Report modal). The file is checked by the BE
  // first — `jsonCheck` holds that verdict — so the user sees what was found,
  // and what will be inferred for an older export, before paying for a run.
  const jsonInputRef = useRef(null);
  const [jsonFile, setJsonFile] = useState(null);
  const [jsonCheck, setJsonCheck] = useState(null);
  const [jsonError, setJsonError] = useState(null);
  const [jsonChecking, setJsonChecking] = useState(false);
  const [jsonImporting, setJsonImporting] = useState(false);
  // The uploader tells us the language; we do not guess it here. An imported
  // document has no survey rows to detect from, so whatever we send becomes
  // SurveyInsight.language verbatim — and that column is what picks the spaCy
  // model and the stop-word list. Defaulting a Spanish file to 'en' means it is
  // tokenized with English stop words, which is the bug this selector fixes.
  const [jsonLanguage, setJsonLanguage] = useState("en");

  // Report deletion: the card awaiting confirmation, and the in-flight ids.
  // Deleting is irreversible and takes a report's project waves with it, so it
  // always goes through the confirm dialog rather than firing on click.
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deletingReport, setDeletingReport] = useState({});
  const [editingInsightId, setEditingInsightId] = useState(null);
  const [editingInsightName, setEditingInsightName] = useState("");
  const [renamingInsight, setRenamingInsight] = useState({});
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12); // Show 12 insights per page
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Report configuration state - supports the JSON format with num_topics, topic_names, num_keywords structure
  // This structure matches the API format for better compatibility and data extraction
  const [reportConfig, setReportConfig] = useState({
    lang: "en", // Language setting
    generalStopWords: [], // General stop words applied to all questions
    num_topics: {}, // Object mapping question titles to number of topics (e.g., {"vision question": 4})
    topic_names: {}, // Object mapping question titles to arrays of topic names
    num_keywords: {}, // Object mapping question titles to number of keywords
    stop_words: {}, // Object mapping question titles to arrays of stop words
    topic_cards: {}, // Object mapping question titles to arrays of topic card details
    questionConfigs: {}, // UI-friendly structure for form configuration (derived from above)
    likert_pillars: {}, // Object mapping likert question titles to pillar values
    open_text_pillars: {}, // Object mapping open text question titles to pillar values
    demog_questions: {}, // Object: { questionKey: { title, is_selected } }
  });
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedInsightId, setSelectedInsightId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const activeUser = useStore((state) => state.activeUser);

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async (page = 1, requestedPageSize = pageSize) => {
    try {
      setLoading(true);
      const response = await insightsApi.getInsightsList({ page, page_size: requestedPageSize });

      const results = response.results || [];
      const count = response.count || 0;

      // Transform API response to match component structure
      const transformedData = results.map(insight => ({
        id: insight.id,
        surveyId: insight.survey_id,
        insightId: insight.insight_id,
        surveyName: insight.survey_title,
        // description: intl.formatMessage({ id: "insight.survey_with_responses", defaultMessage: "Assessment with {count} responses" }, { count: insight.response_count }),
        totalResponses: insight.survey_response_count,
        // completionRate: insight.recipients > 0
        //   ? Math.round((insight.completely_responded / insight.recipients) * 100)
        //   : 100,
        lastUpdated: insight.survey_created,
        // Whether the BE will accept a DELETE from this user (owner or staff).
        // The list also carries reports merely shared with them, which they can
        // read but not delete, so the control is hidden rather than left to 403.
        canDelete: !!insight.can_delete,
        // status: insight.has_reports ? "completed" : "processing",
      }));

      // The backend doesn't always honor the requested page_size, so derive the
      // effective size from the response instead of assuming `requestedPageSize`.
      // Any page that has a `next` is full, so its length is the real page size.
      const effectivePageSize = response.next && results.length > 0
        ? results.length
        : requestedPageSize;

      setInsights(transformedData);
      setCurrentPage(page);
      setTotalCount(count);
      setPageSize(effectivePageSize);
      setTotalPages(Math.max(1, Math.ceil(count / effectivePageSize)));
    } catch (error) {
      console.error("Error loading insights:", error);
      setAlert({ type: "error", message: intl.formatMessage({ id: "insight.failed_to_load", defaultMessage: "Failed to load insights" }) });
    } finally {
      setLoading(false);
    }
  };

  // Record an insight as "generating" so its loading card appears and gets
  // polled. Safe to call with a null id (no-op).
  const markInsightGenerating = (insightId, surveyName = null) => {
    if (insightId == null) return;
    const id = String(insightId);
    setGeneratingInsights(prev => ({
      ...prev,
      [id]: {
        startedAt: Date.now(),
        state: "running",
        step: 1,
        stepLabels: REPORT_STEP_LABELS,
        error: null,
        timedOut: false,
        surveyName: surveyName || prev[id]?.surveyName || null,
      },
    }));
  };

  const dismissGeneratingInsight = (insightId) => {
    const id = String(insightId);
    setGeneratingInsights(prev => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Poll the BE status endpoint (~7s) for each generating insight. On
  // 'finished' we drop the loader and refresh the reports list so the real
  // card appears; on 'errored' we surface the error on the card; and we stop
  // defensively after ~40 min with a "still processing" message. The interval
  // is (re)created only when the SET of generating ids changes; per-step
  // updates are read from the ref inside the loop.
  useEffect(() => {
    const ids = Object.keys(generatingInsights);
    if (ids.length === 0) return undefined;

    let cancelled = false;

    const pollOnce = async () => {
      const current = generatingInsightsRef.current;
      for (const id of Object.keys(current)) {
        const entry = current[id];
        if (!entry || entry.state === "errored" || entry.timedOut) continue;

        // Defensive stop after ~40 min — but NOT for a run parked awaiting
        // confirmation. That one is waiting for a person and may legitimately
        // sit for days; timing it out would strand a Config nobody can resume.
        if (entry.state !== "awaiting_confirmation"
            && Date.now() - entry.startedAt > REPORT_STATUS_TIMEOUT_MS) {
          setGeneratingInsights(prev =>
            prev[id] ? { ...prev, [id]: { ...prev[id], timedOut: true } } : prev
          );
          continue;
        }

        try {
          const requestedAt = Date.now();
          const statusData = await insightsApi.getInsightStatus(id);
          if (cancelled) return;

          if (statusData?.state === "finished") {
            dismissGeneratingInsight(id);
            loadInsights(currentPage);
            continue;
          }

          setGeneratingInsights(prev => {
            if (!prev[id]) return prev;
            // A poll sent before the pillars were confirmed can land after it
            // and would put the card back into review. The BE leaves the
            // review state the moment confirm returns, so only a stale answer
            // can still say so.
            if (prev[id].confirmedAt && requestedAt < prev[id].confirmedAt) return prev;
            return {
              ...prev,
              [id]: {
                ...prev[id],
                state: statusData?.state || prev[id].state,
                step: statusData?.step || prev[id].step,
                // Only set while awaiting confirmation — the config holding the
                // tagging to review.
                configId: statusData?.config_id ?? prev[id].configId,
                stepLabels:
                  Array.isArray(statusData?.step_labels) && statusData.step_labels.length
                    ? statusData.step_labels.map((l) => (typeof l === "string" ? l : l?.label ?? ""))
                    : prev[id].stepLabels,
                error:
                  statusData?.state === "errored"
                    ? statusData?.error || "Report generation failed."
                    : prev[id].error,
              },
            };
          });
        } catch (e) {
          // Transient (e.g. 404 before the job registers, or a network blip) —
          // keep polling; the ~40 min timeout bounds how long we retry.
          console.warn("Report status poll failed for insight", id, e?.message || e);
        }
      }
    };

    pollOnce();
    const timer = setInterval(pollOnce, REPORT_STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(generatingInsights).join(",")]);

  const handleGenerateReport = async (insightId, name = null) => {
    try {
      setGenerating(prev => ({ ...prev, [insightId]: true }));

      // Use insight ID 1 for testing (replace with actual mapping later)
      // const insightId = 1;
      console.log(`Generating report for insight ID: ${insightId}`);

      const result = await api.generateInsightReport(insightId, name);
      console.log("Report generation response:", result);

      // `insightId` here is actually the config id (the generate-report route is
      // keyed by config_id and its response carries no insight_id), so resolve
      // the real insight_id from the selected config to drive the status card.
      const selectedConfig = surveysForModal.find(c => c.id === insightId);
      markInsightGenerating(selectedConfig?.insightId, selectedConfig?.insightTitle || selectedConfig?.surveyTitle);

      setAlert({
        type: "success",
        // message: result.message || intl.formatMessage({ id: "insight.report_generation_started", defaultMessage: "Report generation started successfully" })
        message: intl.formatMessage({ id: "insight.report_generation_started", defaultMessage: "Report generation started successfully. It may take 15 to 25 minutes to complete. The new report will be available in this page after completion." })
      });
      setSurveyModalOpen(false);
      setSelectedSurveyId(null);
      setReportConfig({
        lang: "en",
        generalStopWords: [],
        num_topics: {},
        topic_names: {},
        num_keywords: {},
        stop_words: {},
        topic_cards: {},
        questionConfigs: {},
        likert_pillars: {},
        open_text_pillars: {},
      });
      setCurrentQuestionIndex(0);

      // Optionally reload insights to show the new report
      // await loadInsights();
    } catch (error) {
      console.error("Error generating report:", error);
      setAlert({
        type: "error",
        message: error.message || intl.formatMessage({ id: "insight.failed_to_generate_report", defaultMessage: "Failed to generate report" })
      });
    } finally {
      setGenerating(prev => ({ ...prev, [insightId]: false }));
    }
  };

  const handleCloseSurveyModal = () => {
    setSurveyModalOpen(false);
    setSurveyModalPage(1);
    setSurveyModalSearch("");
    setSelectedSurveyId(null);
    setReportName("");
    setEditingConfigId(null);
    setEditingConfigName("");
  };

  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
  };

  const handleConfigSurvey = async (surveyId) => {
    setConfigLoading(true);
    try {
      setCurrentQuestionIndex(0);

      // Try to fetch existing config if there's already an insight for this survey
      // NOTE: The API uses insight_id which may be different from survey_id
      // For now, we're using survey_id and assuming they match or the backend handles the mapping
      let existingConfig = null;
      try {
        console.log("Attempting to fetch config for survey ID:", surveyId);
        existingConfig = await api.getInsightConfig(surveyId);
        console.log("Fetched existing config:", existingConfig);
      } catch (error) {
        console.log("No existing config found, will create new one. Error:", error?.message || error);
        // This is expected if no insight exists yet for this survey
      }

      if (existingConfig && existingConfig.metadata) {
        // Transform API config to UI state
        // Extract question titles from num_topics keys
        const metadata = existingConfig.metadata;
        const insightId = existingConfig.insight_id;
        const questionTitles = Object.keys(metadata.num_topics || {});

        console.log("Metadata from API:", metadata);
        console.log("Question titles from config:", questionTitles);

        // Validate config structure
        const validationErrors = validateConfigStructure(metadata);
        if (validationErrors.length > 0) {
          console.warn("Config validation errors:", validationErrors);
          setAlert({
            type: "warning",
            message: `Configuration loaded with validation warnings: ${validationErrors.join(', ')}`
          });
        }

        // Build questions array from config
        const questions = questionTitles.map((title, index) => ({
          id: title, // Use title as ID since we don't have actual IDs
          title: title,
          type: "openText"
        }));

        setSelectedSurvey({ questions, id: surveyId });

        // Build question configs with enhanced data extraction
        const questionConfigs = {};
        questionTitles.forEach(questionTitle => {
          const numTopics = metadata.num_topics?.[questionTitle] || 3; // Default to 3 topics
          const numKeywords = metadata.num_keywords?.[questionTitle] || 8; // Default to 8 keywords
          const stopWords = metadata.stop_words?.[questionTitle] || [];
          const topicTitles = metadata.topic_names?.[questionTitle] || [];

          // Ensure topicTitles array matches numTopics length
          const adjustedTopicTitles = Array.isArray(topicTitles) && topicTitles.length === numTopics
            ? topicTitles
            : Array(numTopics).fill("").map((_, idx) => topicTitles[idx] || "");

          questionConfigs[questionTitle] = {
            numTopics,
            numKeywords,
            stopWords,
            topicTitles: adjustedTopicTitles,
          };

          console.log(`Enhanced config for question "${questionTitle}":`, {
            ...questionConfigs[questionTitle],
            hasValidTopicNames: adjustedTopicTitles.every(title => title.trim() !== ""),
            totalConfiguredTopics: adjustedTopicTitles.length,
            sourceData: {
              numTopics: !!metadata.num_topics?.[questionTitle],
              numKeywords: !!metadata.num_keywords?.[questionTitle],
              stopWords: !!metadata.stop_words?.[questionTitle],
              topicNames: !!metadata.topic_names?.[questionTitle],
            }
          });
        });

        const newConfig = {
          insightId: insightId,
          lang: metadata.lang || "en",
          generalStopWords: metadata.stop_words?.default || [],
          num_topics: metadata.num_topics || {},
          topic_names: metadata.topic_names || {},
          num_keywords: metadata.num_keywords || {},
          stop_words: metadata.stop_words || {},
          topic_cards: metadata.topic_cards || {},
          questionConfigs,
          likert_pillars: metadata.likert_pillars || {},
          open_text_pillars: metadata.open_text_pillars || {},
          demog_questions: metadata.demog_questions || {},
        };

        console.log("Setting report config:", newConfig);
        setReportConfig(newConfig);
        setHasExistingConfig(true);
      } else {
        // No existing config - show empty state or allow user to add questions
        console.log("No existing config found");
        setSelectedSurvey({ questions: [], id: surveyId });
        setReportConfig({
          insightId: null,
          lang: "en",
          generalStopWords: [],
          num_topics: {},
          topic_names: {},
          num_keywords: {},
          stop_words: {},
          topic_cards: {},
          questionConfigs: {},
          likert_pillars: {},
          open_text_pillars: {},
          demog_questions: {},
        });
        setHasExistingConfig(false);
      }

      setConfigModalOpen(true);
    } catch (error) {
      setAlert({ type: "error", message: intl.formatMessage({ id: "insight.failed_to_load_config", defaultMessage: "Failed to load assessment configuration" }) });
      console.error("Error loading survey config:", error);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfiguration = async (generateReport = false) => {
    if (selectedSurveyId) {
      try {
        // Transform UI state to API format
        const metadata = {
          lang: reportConfig.lang,
          ngram: 2,
          mallet: false,
          passes: 1,
          chunksize: 2000,
          min_count: 5,
          threshold: 100,
          mallet_path: null,
          n_common_words: 10,
          vectorize_type: "count",
          n_representative_response: 30,
          num_topics: {},
          stop_words: {
            default: reportConfig.generalStopWords
          },
          topic_names: {},
          num_keywords: {},
          likert_pillars: Object.fromEntries(
            Object.entries(reportConfig.likert_pillars || {}).map(([k, v]) => [k, normalizePillar(v)])
          ),
          open_text_pillars: Object.fromEntries(
            Object.entries(reportConfig.open_text_pillars || {}).map(([k, v]) => [k, normalizePillar(v)])
          ),
          demog_questions: reportConfig.demog_questions || {},
        };

        // Map question configs to API format
        // First, copy over the raw config data if available
        if (reportConfig.num_topics) {
          metadata.num_topics = { ...metadata.num_topics, ...reportConfig.num_topics };
        }
        if (reportConfig.topic_names) {
          metadata.topic_names = { ...metadata.topic_names, ...reportConfig.topic_names };
        }
        if (reportConfig.num_keywords) {
          metadata.num_keywords = { ...metadata.num_keywords, ...reportConfig.num_keywords };
        }
        if (reportConfig.stop_words) {
          metadata.stop_words = { ...metadata.stop_words, ...reportConfig.stop_words };
        }

        // Then map question configs to API format (for newly configured questions)
        Object.entries(reportConfig.questionConfigs).forEach(([questionTitle, config]) => {
          console.log(`Saving config for question "${questionTitle}":`, config);
          metadata.num_topics[questionTitle] = config.numTopics;
          metadata.num_keywords[questionTitle] = config.numKeywords;
          metadata.stop_words[questionTitle] = config.stopWords;
          metadata.topic_names[questionTitle] = config.topicTitles;
        });

        console.log("Saving metadata to insight ID:", selectedSurveyId);
        console.log("Metadata being saved:", metadata);

        // Save the config
        const response = await api.updateInsightConfig(selectedSurveyId, metadata);
        console.log("Save response:", response);

        setAlert({ type: "success", message: intl.formatMessage({ id: "insight.config_saved", defaultMessage: "Configuration saved successfully" }) });
        setConfigModalOpen(false);

        if (generateReport) {
          await handleGenerateReport(selectedSurveyId);
        }
      } catch (error) {
        setAlert({ type: "error", message: intl.formatMessage({ id: "insight.failed_to_save_config", defaultMessage: "Failed to save configuration" }) });
        console.error("Error saving config:", error);
      }
    }
  };

  const handleDirectGeneration = async () => {
    // Direct generation without configuration
    if (selectedSurveyId) {
      await handleGenerateReport(selectedSurveyId, reportName || null);
    }
  };

  const handleSaveAndGenerate = async () => {
    // Save configuration and then generate
    await handleSaveConfiguration(true);
  };

  const fetchSurveysForModal = async (page = 1, search = "") => {
    setSurveyModalLoading(true);
    try {
      // Fetch insight configs from /api/v1/insights/configs/?all=true
      const response = await insightsApi.getInsightConfigs({
        page,
        page_size: 10,
        search: search
      });
      console.log("Insight configs for report generation:", response);

      // Transform API response to match component structure
      const transformedData = response.results?.map(config => ({
        id: config.id,
        insightId: config.insight_id,
        surveyId: config.survey_id,
        surveyTitle: config.survey_title,
        insightTitle: config.insight_title,
        insightCreated: config.insight_created,
        metadata: config.metadata,
        hasReport: config.has_report,
        // Extract question count from metadata
        questionCount: config.metadata?.num_topics ? Object.keys(config.metadata.num_topics).length : 0,
      })) || [];

      setSurveysForModal(transformedData);
      setSurveyModalTotalPages(Math.ceil((response.count || 0) / 10));
      setSurveyModalTotalItems(response.count || 0);
    } catch (error) {
      console.error("Error loading insight configs:", error);
      setAlert({ type: "error", message: intl.formatMessage({ id: "insight.failed_to_load_surveys", defaultMessage: "Failed to load assessments for report generation" }) });
    } finally {
      setSurveyModalLoading(false);
    }
  };

  const handleViewDetails = (insightId) => {
    // Navigate to insight details page using the insight ID
    navigate(`/insights/${insightId}`);
  };

  const handleOpenDownloadDialog = (insightId) => {
    setSelectedInsightId(insightId);
    setDownloadDialogOpen(true);
  };

  const handleCloseDownloadDialog = () => {
    setDownloadDialogOpen(false);
    setSelectedInsightId(null);
  };

  const handleDownload = async (downloadType) => {
    if (!selectedInsightId) return;

    try {
      setDownloading(true);
      // Fetch the report data using the same API as InsightDetailsPage
      const data = await insightsApi.getInsightReport(selectedInsightId);
      let typeData = data;
      if (downloadType === 'likert') {
        typeData = data.results?.likert_skirt;
      } else if (downloadType === 'mcq') {
        typeData = data.results?.stats?.questions;
      } else if (downloadType === 'openText') {
        typeData = data.results?.topics;
      }
      // For now, all download types return the same JSON
      const jsonData = JSON.stringify(typeData, null, 2);

      // Generate filename based on download type
      const filenames = {
        all: `insight_report_all_data_${selectedInsightId}_${Date.now()}.json`,
        likert: `insight_report_likert_scale_${selectedInsightId}_${Date.now()}.json`,
        mcq: `insight_report_mcq_${selectedInsightId}_${Date.now()}.json`,
        openText: `insight_report_open_text_${selectedInsightId}_${Date.now()}.json`,
      };

      const filename = filenames[downloadType] || filenames.all;

      // Download the file
      fileDownload(jsonData, filename);

      setAlert({
        type: "success",
        message: intl.formatMessage({ id: "insight.download_started", defaultMessage: "Download started successfully" })
      });

      handleCloseDownloadDialog();
    } catch (error) {
      console.error("Error downloading report:", error);
      setAlert({
        type: "error",
        message: intl.formatMessage({ id: "insight.failed_to_download", defaultMessage: "Failed to download report" })
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleModalPageChange = (event, page) => {
    setSurveyModalPage(page);
    fetchSurveysForModal(page, surveyModalSearch);
  };

  const handleModalSearch = (searchQuery) => {
    setSurveyModalSearch(searchQuery);
    setSurveyModalPage(1);
    fetchSurveysForModal(1, searchQuery);
  };

  const handleStartEditConfig = (configId, currentName) => {
    setEditingConfigId(configId);
    setEditingConfigName(currentName || "");
  };

  const handleCancelEditConfig = () => {
    setEditingConfigId(null);
    setEditingConfigName("");
  };

  const handleSaveConfigName = async (configId) => {
    if (!editingConfigName.trim()) {
      setAlert({
        type: "error",
        message: intl.formatMessage({ id: "insight.config_name_required", defaultMessage: "Config name cannot be empty" })
      });
      return;
    }

    try {
      setRenamingConfig(prev => ({ ...prev, [configId]: true }));
      const response = await insightsApi.renameConfig(configId, editingConfigName.trim());

      // Update the local state
      setSurveysForModal(prev => prev.map(config =>
        config.id === configId
          ? { ...config, surveyTitle: response.name }
          : config
      ));

      setAlert({
        type: "success",
        message: intl.formatMessage({ id: "insight.config_renamed", defaultMessage: "Config renamed successfully" })
      });

      handleCancelEditConfig();
    } catch (error) {
      console.error("Error renaming config:", error);
      setAlert({
        type: "error",
        message: error.message || intl.formatMessage({ id: "insight.failed_to_rename_config", defaultMessage: "Failed to rename config" })
      });
    } finally {
      setRenamingConfig(prev => ({ ...prev, [configId]: false }));
    }
  };

  const handleStartEditReport = (reportId, currentName) => {
    setEditingReportId(reportId);
    setEditingReportName(currentName || "");
  };

  const handleCancelEditReport = () => {
    setEditingReportId(null);
    setEditingReportName("");
  };

  const handleSaveReportName = async (reportId) => {
    if (!editingReportName.trim()) {
      setAlert({
        type: "error",
        message: intl.formatMessage({ id: "insight.report_name_required", defaultMessage: "Report name cannot be empty" })
      });
      return;
    }

    try {
      setRenamingReport(prev => ({ ...prev, [reportId]: true }));
      const response = await insightsApi.renameReport(reportId, editingReportName.trim());

      // Update the local state
      setInsights(prev => prev.map(insight =>
        insight.id === reportId
          ? { ...insight, surveyName: response.name }
          : insight
      ));

      setAlert({
        type: "success",
        message: intl.formatMessage({ id: "insight.report_renamed", defaultMessage: "Report renamed successfully" })
      });

      handleCancelEditReport();
    } catch (error) {
      console.error("Error renaming report:", error);
      setAlert({
        type: "error",
        message: error.message || intl.formatMessage({ id: "insight.failed_to_rename_report", defaultMessage: "Failed to rename report" })
      });
    } finally {
      setRenamingReport(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const handleAskDeleteReport = (report) => {
    setReportToDelete({ id: report.id, name: report.surveyName });
  };

  const handleCancelDeleteReport = () => {
    setReportToDelete(null);
  };

  // Delete the report the dialog is holding. The BE takes its project waves,
  // its shares and its RAG vectors with it and reports what went, so the
  // success message can say whether a wave was removed too.
  const handleConfirmDeleteReport = async () => {
    if (!reportToDelete) return;
    const { id, name } = reportToDelete;

    try {
      setDeletingReport(prev => ({ ...prev, [id]: true }));
      const result = await insightsApi.deleteReport(id);

      const waveCount = result?.deleted?.waves?.length || 0;
      setAlert({
        type: "success",
        message: waveCount > 0
          ? intl.formatMessage(
            {
              id: "insight.report_deleted_with_waves",
              defaultMessage: "Report \"{name}\" deleted, along with {count, plural, one {# project wave} other {# project waves}}."
            },
            { name: name || id, count: waveCount }
          )
          : intl.formatMessage(
            { id: "insight.report_deleted", defaultMessage: "Report \"{name}\" deleted." },
            { name: name || id }
          )
      });

      setReportToDelete(null);

      // Deleting the only card on a page would otherwise leave it empty, so
      // step back one page in that case; the row count has already changed, so
      // reload rather than splicing local state.
      const isLastOnPage = insights.length === 1 && currentPage > 1;
      const nextPage = isLastOnPage ? currentPage - 1 : currentPage;
      if (isLastOnPage) setCurrentPage(nextPage);
      await loadInsights(nextPage, pageSize);
    } catch (error) {
      console.error("Error deleting report:", error);
      setAlert({
        type: "error",
        message: error.response?.data?.error
          || error.message
          || intl.formatMessage({ id: "insight.failed_to_delete_report", defaultMessage: "Failed to delete report" })
      });
    } finally {
      setDeletingReport(prev => ({ ...prev, [id]: false }));
    }
  };

  // The page's ONLY generate entry point: the selection modal opened in 'full'
  // mode, whose confirm runs BE's single build_insight job (config + report +
  // risk data). The modal's 'default' and 'v2' branches — and the separate
  // survey-selection / config-editor modals — are no longer reachable from the
  // toolbar; they are left in place for now rather than ripped out with this
  // change.
  const handleOpenGenerateFullReportModal = () => {
    // Re-seed from storage on every open rather than only at page mount, so a
    // pillar added in Manage Users shows up here without a reload. initialRows
    // owns the precedence: an in-progress draft, else the saved library.
    setPillarRows(initialPillarRows());
    setGenerateConfigModalOpen(true);
    fetchSurveysForGenerateConfig(1, "");
  };

  const handleCloseGenerateConfigModal = () => {
    setGenerateConfigModalOpen(false);
    setJsonFile(null);
    setJsonCheck(null);
    setJsonError(null);
    if (jsonInputRef.current) jsonInputRef.current.value = "";
    setGenerateConfigPage(1);
    setGenerateConfigSearch("");
    setSelectedConfigSurveyId(null);
    setConfigName("");
    setEditingInsightId(null);
    setEditingInsightName("");
  };

  const fetchSurveysForGenerateConfig = async (page = 1, search = "") => {
    setGenerateConfigLoading(true);
    try {
      // Fetch insights list from /api/v1/insights/
      const response = await insightsApi.getInsightsList2({
        page,
        page_size: 10,
        search: search
      });
      console.log("Insights for generate config:", response);

      // Transform API response to match component structure
      const transformedData = response.results?.map(insight => ({
        id: insight.id,
        surveyId: insight.survey,
        surveyEngineUid: insight.survey_engine_uid,
        title: insight.title,
        created: insight.created,
        responseCount: insight.response_count,
        hasReports: insight.has_reports,
        recipients: insight.recipients,
        completelyResponded: insight.completely_responded,
      })) || [];

      setGenerateConfigSurveys(transformedData);
      setGenerateConfigTotalPages(Math.ceil((response.count || 0) / 10));
      setGenerateConfigTotalItems(response.count || 0);
    } catch (error) {
      console.error("Error loading insights for config:", error);
      setAlert({ type: "error", message: intl.formatMessage({ id: "insight.failed_to_load_surveys", defaultMessage: "Failed to load assessments" }) });
    } finally {
      setGenerateConfigLoading(false);
    }
  };

  const handleGenerateConfigPageChange = (event, page) => {
    setGenerateConfigPage(page);
    fetchSurveysForGenerateConfig(page, generateConfigSearch);
  };

  const handleGenerateConfigSearch = (searchQuery) => {
    setGenerateConfigSearch(searchQuery);
    setGenerateConfigPage(1);
    fetchSurveysForGenerateConfig(1, searchQuery);
  };

  const handleStartEditInsight = (insightId, currentName) => {
    setEditingInsightId(insightId);
    setEditingInsightName(currentName || "");
  };

  const handleCancelEditInsight = () => {
    setEditingInsightId(null);
    setEditingInsightName("");
  };

  const handleSaveInsightName = async (insightId) => {
    if (!editingInsightName.trim()) {
      setAlert({
        type: "error",
        message: intl.formatMessage({ id: "insight.insight_name_required", defaultMessage: "Insight name cannot be empty" })
      });
      return;
    }

    try {
      setRenamingInsight(prev => ({ ...prev, [insightId]: true }));
      const response = await insightsApi.renameInsight(insightId, editingInsightName.trim());

      // Update the local state
      setGenerateConfigSurveys(prev => prev.map(insight =>
        insight.id === insightId
          ? { ...insight, title: response.name }
          : insight
      ));

      setAlert({
        type: "success",
        message: intl.formatMessage({ id: "insight.insight_renamed", defaultMessage: "Insight renamed successfully" })
      });

      handleCancelEditInsight();
    } catch (error) {
      console.error("Error renaming insight:", error);
      setAlert({
        type: "error",
        message: error.message || intl.formatMessage({ id: "insight.failed_to_rename_insight", defaultMessage: "Failed to rename insight" })
      });
    } finally {
      setRenamingInsight(prev => ({ ...prev, [insightId]: false }));
    }
  };

  const handleBuildInsight = async () => {
    if (!selectedConfigSurveyId) return;
    if (pillarsValidation.formError) {
      setAlert({ type: "error", message: pillarsValidation.formError });
      return;
    }

    try {
      setTriggeringAutoconfig(prev => ({ ...prev, [selectedConfigSurveyId]: true }));

      // Declare the taxonomy BEFORE the pipeline starts — autoconfig reads it
      // from raw_input, so a save after the trigger would be too late. Unlike
      // the upload path this can be its own request, because the build is only
      // triggered below.
      if (Object.keys(pillarsPayload).length) {
        await insightsApi.saveInsightPillars(selectedConfigSurveyId, pillarsPayload);
      }

      console.log(`Triggering full insight pipeline for insight ID: ${selectedConfigSurveyId}`);

      const result = await insightsApi.buildInsight(selectedConfigSurveyId, {
        name: configName || null,
        // Stop once the questions are tagged. The run parks there until the
        // tagging is confirmed, then resumes at `topics`.
        until: "pillars",
      });

      console.log("build_insight trigger response:", result);

      // The response carries insight_id (the same one used in the route), so the
      // step-by-step status card can be shown and polled straight away.
      markInsightGenerating(result?.insight_id ?? selectedConfigSurveyId, configName || null);

      setAlert({
        type: "success",
        message: result.message || intl.formatMessage({
          id: "insight.full_pipeline_triggered",
          defaultMessage: "Full pipeline triggered successfully (config, report and risk data). It may take 20 to 40 minutes to complete. The report appears on this page when it is ready; the Risk tab fills in a few minutes later."
        })
      });

      clearPillarsDraft();
      setPillarRows(initialPillarRows());
      handleCloseGenerateConfigModal();
    } catch (error) {
      console.error("Error triggering the full insight pipeline:", error);
      setAlert({
        type: "error",
        message: error.response?.data?.error
          || error.response?.data?.detail
          || error.message
          || intl.formatMessage({ id: "insight.failed_to_trigger_full_pipeline", defaultMessage: "Failed to trigger the full pipeline" })
      });
    } finally {
      setTriggeringAutoconfig(prev => ({ ...prev, [selectedConfigSurveyId]: false }));
    }
  };

  // ── Insight removal ─────────────────────────────────────────────────────
  const handleAskDeleteInsight = (insight) => {
    setInsightToDelete({ id: insight.id, title: insight.title, hasReports: insight.hasReports });
  };

  const handleCancelDeleteInsight = () => {
    setInsightToDelete(null);
  };

  const handleConfirmDeleteInsight = async () => {
    if (!insightToDelete) return;
    const { id, title } = insightToDelete;

    try {
      setDeletingInsight(prev => ({ ...prev, [id]: true }));
      const result = await insightsApi.deleteInsight(id);

      const removed = result?.deleted || {};
      const riskRows = removed["insights.RiskPredictionItem"] || 0;
      const reports = removed["insights.Report"] || 0;
      setAlert({
        type: "success",
        message: intl.formatMessage(
          {
            id: "insight.insight_deleted",
            defaultMessage: "Insight \"{name}\" removed, along with {reports, plural, =0 {no reports} one {# report} other {# reports}} and {rows, plural, =0 {no risk rows} one {# risk row} other {# risk rows}}."
          },
          { name: title || id, reports, rows: riskRows }
        )
      });

      setInsightToDelete(null);
      // Selecting a row that no longer exists would arm a generate against a
      // dead id, so drop the selection and any loading card for it.
      if (selectedConfigSurveyId === id) setSelectedConfigSurveyId(null);
      dismissGeneratingInsight(id);
      // Both lists change: the modal's insights and the page's report cards.
      await fetchSurveysForGenerateConfig(generateConfigPage, generateConfigSearch);
      await loadInsights(currentPage, pageSize);
    } catch (error) {
      console.error("Error deleting insight:", error);
      setAlert({
        type: "error",
        message: error.response?.data?.error
          || error.response?.data?.detail
          || error.message
          || intl.formatMessage({ id: "insight.failed_to_delete_insight", defaultMessage: "Failed to remove insight" })
      });
    } finally {
      setDeletingInsight(prev => ({ ...prev, [id]: false }));
    }
  };

  // ── JSON import ─────────────────────────────────────────────────────────
  const resetJsonImport = () => {
    setJsonFile(null);
    setJsonCheck(null);
    setJsonError(null);
    if (jsonInputRef.current) jsonInputRef.current.value = "";
  };

  // Step 1: hand the file to the BE for a format check. Nothing is created and
  // no LLM call is made, so this is safe to run on every file the user picks.
  const handleJsonFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setJsonFile(file);
    setJsonCheck(null);
    setJsonError(null);

    try {
      setJsonChecking(true);
      const result = await insightsApi.uploadRawInput(file, {
        title: configName || null,
        checkOnly: true,
        language: jsonLanguage,
      });
      setJsonCheck(result);
    } catch (error) {
      console.error("JSON format check failed:", error);
      setJsonError(
        error.response?.data?.error
        || error.message
        || intl.formatMessage({ id: "insight.json_check_failed", defaultMessage: "Could not read that file" })
      );
    } finally {
      setJsonChecking(false);
    }
  };

  // Step 2: the real import. The BE normalizes the document (filling in an
  // older export's likert_groups / likert_pillars), creates a SurveyInsight
  // with a placeholder survey, and runs config -> report -> signals against it,
  // so progress is the same status card as every other generate flow.
  const [pillarRows, setPillarRows] = useState(initialPillarRows);
  // {insightId, configId} while reviewing a parked run's pillar tagging.
  const [reviewTarget, setReviewTarget] = useState(null);

  // The optional pillar taxonomy typed into this modal. Applies to whichever
  // input is used — an uploaded JSON file or a selected insight.
  const pillarsPayload = pillarsToPayload(pillarRows);
  const pillarsValidation = validatePillars(pillarRows);

  const handlePillarRowsChange = (rows) => {
    setPillarRows(rows);
    savePillarsDraft(rows);
  };

  const handleImportJson = async () => {
    if (!jsonFile) return;
    if (pillarsValidation.formError) {
      setJsonError(pillarsValidation.formError);
      return;
    }
    try {
      setJsonImporting(true);
      const result = await insightsApi.uploadRawInput(jsonFile, {
        title: configName || null,
        language: jsonLanguage,
        pillarsDef: pillarsPayload,
      });

      markInsightGenerating(result?.insight_id, result?.name || jsonFile.name);

      const notes = result?.notes || [];
      setAlert({
        type: "success",
        message: [
          intl.formatMessage(
            {
              id: "insight.json_imported",
              defaultMessage: "\"{name}\" imported as insight #{id}. The full pipeline is running — the report appears on this page when it is ready."
            },
            { name: result?.name || jsonFile.name, id: result?.insight_id }
          ),
          ...notes,
        ].join(" ")
      });

      resetJsonImport();
      clearPillarsDraft();
      setPillarRows(initialPillarRows());
      handleCloseGenerateConfigModal();
    } catch (error) {
      console.error("JSON import failed:", error);
      setJsonError(
        error.response?.data?.error
        || error.message
        || intl.formatMessage({ id: "insight.json_import_failed", defaultMessage: "Failed to import the file" })
      );
    } finally {
      setJsonImporting(false);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    loadInsights(page, pageSize);
  };

  // Get open text questions from config
  // Validate config structure
  const validateConfigStructure = (config) => {
    const errors = [];

    if (!config.num_topics || typeof config.num_topics !== 'object') {
      errors.push('num_topics must be an object');
    }
    if (!config.topic_names || typeof config.topic_names !== 'object') {
      errors.push('topic_names must be an object');
    }
    if (!config.num_keywords || typeof config.num_keywords !== 'object') {
      errors.push('num_keywords must be an object');
    }

    // Check consistency between num_topics and topic_names
    const numTopicsKeys = Object.keys(config.num_topics || {});
    const topicNamesKeys = Object.keys(config.topic_names || {});
    const numKeywordsKeys = Object.keys(config.num_keywords || {});

    const allKeys = new Set([...numTopicsKeys, ...topicNamesKeys, ...numKeywordsKeys]);

    for (const questionTitle of allKeys) {
      const numTopics = config.num_topics?.[questionTitle];
      const topicNames = config.topic_names?.[questionTitle];
      const numKeywords = config.num_keywords?.[questionTitle];

      if (numTopics !== undefined && (!Array.isArray(topicNames) || topicNames.length !== numTopics)) {
        errors.push(`Question "${questionTitle}": topic_names array length (${topicNames?.length || 0}) doesn't match num_topics (${numTopics})`);
      }
      if (numKeywords !== undefined && (typeof numKeywords !== 'number' || numKeywords < 1)) {
        errors.push(`Question "${questionTitle}": num_keywords must be a positive number`);
      }
    }

    return errors;
  };

  const getOpenTextQuestions = () => {
    // Extract question titles from all config sources
    const questionTitles = new Set([
      ...Object.keys(reportConfig.questionConfigs || {}),
      ...Object.keys(reportConfig.num_topics || {}),
      ...Object.keys(reportConfig.topic_names || {}),
      ...Object.keys(reportConfig.num_keywords || {})
    ]);

    return Array.from(questionTitles).map(title => ({
      id: title,
      title: title,
      type: "openText"
    }));
  };

  const PILLAR_OPTIONS = getDefaultPillars();

  const normalizePillar = (value) => {
    if (!value) return "";
    const match = PILLAR_OPTIONS.find(
      (opt) => opt.toLowerCase() === value.toLowerCase()
    );
    return match || value;
  };

  const getLikertQuestions = () => {
    return Object.keys(reportConfig.likert_pillars || {}).map(title => ({
      id: title,
      title: title,
      pillar: normalizePillar(reportConfig.likert_pillars[title]),
    }));
  };

  const getOpenTextQuestionsForPillars = () => {
    return Object.keys(reportConfig.open_text_pillars || {}).map(title => ({
      id: title,
      title: title,
      pillar: normalizePillar(reportConfig.open_text_pillars[title]),
    }));
  };

  const handleLikertPillarChange = (questionTitle, pillarValue) => {
    setReportConfig(prev => ({
      ...prev,
      likert_pillars: {
        ...prev.likert_pillars,
        [questionTitle]: pillarValue,
      },
    }));
  };

  const handleOpenTextPillarChange = (questionTitle, pillarValue) => {
    setReportConfig(prev => ({
      ...prev,
      open_text_pillars: {
        ...prev.open_text_pillars,
        [questionTitle]: pillarValue,
      },
    }));
  };

  const handleDemogSelectionChange = (questionKey, isSelected) => {
    setReportConfig(prev => ({
      ...prev,
      demog_questions: {
        ...prev.demog_questions,
        [questionKey]: {
          ...(prev.demog_questions?.[questionKey] || { title: questionKey }),
          is_selected: isSelected,
        },
      },
    }));
  };

  const handleDemogTitleChange = (questionKey, newTitle) => {
    setReportConfig(prev => ({
      ...prev,
      demog_questions: {
        ...prev.demog_questions,
        [questionKey]: {
          ...(prev.demog_questions?.[questionKey] || { is_selected: false }),
          title: newTitle,
        },
      },
    }));
  };

  const handleNextQuestion = () => {
    const openTextQuestions = getOpenTextQuestions();
    if (currentQuestionIndex < openTextQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const updateQuestionConfig = (questionTitle, field, value) => {
    setReportConfig(prev => ({
      ...prev,
      questionConfigs: {
        ...prev.questionConfigs,
        [questionTitle]: {
          ...prev.questionConfigs[questionTitle],
          [field]: value,
        },
      },
    }));
  };

  const handleNumTopicsChange = (questionTitle, newNumTopics) => {
    const numTopics = parseInt(newNumTopics) || 3;
    const currentConfig = reportConfig.questionConfigs[questionTitle] || {};
    const currentTitles = currentConfig.topicTitles || [];

    // Adjust topic titles array to match new number of topics
    const newTitles = Array(numTopics).fill("").map((_, idx) => currentTitles[idx] || "");

    setReportConfig(prev => ({
      ...prev,
      questionConfigs: {
        ...prev.questionConfigs,
        [questionTitle]: {
          ...prev.questionConfigs[questionTitle],
          numTopics,
          topicTitles: newTitles,
        },
      },
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      // hour: '2-digit',
      // minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 64px)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
            <Analytics sx={{ fontSize: 32 }} />
            <FormattedMessage id="insight.your_reports" defaultMessage="Your Reports" />
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* One entry point: the whole pipeline in one job (config, report and
                the risk data), or an imported raw_input JSON file. The older
                Generate Config / Generate Report / Generate Report V2 buttons
                were removed — each ran a subset of this, and their modals are
                now unreachable. */}
            {activeUser?.is_staff && (
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={handleOpenGenerateFullReportModal}
                disabled={loading}
              >
                <FormattedMessage id="insight.generate_full_report" defaultMessage="Generate Full Report" />
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {/* Insights Grid */}
      <Grid container spacing={3}>
        {/* Loading cards for in-progress generations (top of the grid) */}
        {Object.entries(generatingInsights).map(([id, entry]) => (
          <Grid item xs={12} md={6} lg={4} key={`generating-${id}`}>
            <GeneratingReportCard
              entry={entry}
              onDismiss={() => dismissGeneratingInsight(id)}
              onReview={() => setReviewTarget({ insightId: id, configId: entry.configId })}
            />
          </Grid>
        ))}
        {insights.map((insight) => (
          <Grid item xs={12} md={6} lg={4} key={insight.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                {/* Survey Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  {editingReportId === insight.id ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                      <TextField
                        size="small"
                        value={editingReportName}
                        onChange={(e) => setEditingReportName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveReportName(insight.id);
                          } else if (e.key === "Escape") {
                            handleCancelEditReport();
                          }
                        }}
                        autoFocus
                        disabled={renamingReport[insight.id]}
                        sx={{ flex: 1, minWidth: 200 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleSaveReportName(insight.id)}
                        disabled={renamingReport[insight.id]}
                        color="primary"
                      >
                        {renamingReport[insight.id] ? <CircularProgress size={16} /> : <CheckIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelEditReport}
                        disabled={renamingReport[insight.id]}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {insight.surveyName}
                      </Typography>
                      {activeUser?.is_staff && (
                        <Tooltip title={intl.formatMessage({ id: "insight.rename_report", defaultMessage: "Rename report" })}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditReport(insight.id, insight.surveyName);
                            }}
                            disabled={renamingReport[insight.id]}
                            sx={{
                              padding: 0.25,
                              "&:hover": {
                                bgcolor: "action.hover"
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}

                </Box>


                {/* Key Metrics */}
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Paper
                        sx={{
                          p: 2,
                          textAlign: "center",
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "grey.200",
                          borderRadius: 2,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          transition: "box-shadow 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.6rem",
                            fontWeight: 400,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            mb: 0.5,
                          }}
                        >
                          Created at
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "0.875rem",
                          }}
                        >
                          {formatDate(insight.lastUpdated)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "primary.50" }}>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: "bold" }}>
                          {insight.totalResponses.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <FormattedMessage id="insight.responses" defaultMessage="Responses" />
                        </Typography>
                      </Paper>
                    </Grid>

                  </Grid>
                </Box>

                {/* Topics */}

                {/* Demographics Summary */}

                <Divider sx={{ my: 1 }} />

                {/* Last Updated */}
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  component="a"
                  href={`/insights/${insight.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleViewDetails(insight.id);
                  }}
                  sx={{ flex: 1 }}
                >
                  <FormattedMessage id="insight.view_details" defaultMessage="View Details" />
                </Button>
                {insight.canDelete && (
                <Tooltip title={intl.formatMessage({ id: "insight.delete_report", defaultMessage: "Delete report" })}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAskDeleteReport(insight);
                    }}
                    disabled={!!deletingReport[insight.id]}
                    aria-label={intl.formatMessage({ id: "insight.delete_report", defaultMessage: "Delete report" })}
                  >
                    {deletingReport[insight.id] ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Empty State */}
      {insights.length === 0 && !loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            textAlign: "center",
          }}
        >
          <Analytics sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            <FormattedMessage id="insight.no_insights_available" defaultMessage="No insights available" />
          </Typography>
        </Box>
      )}

      {/* Remove Insight Confirmation */}
      <Dialog
        open={!!insightToDelete}
        onClose={handleCancelDeleteInsight}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <FormattedMessage id="insight.delete_insight_title" defaultMessage="Remove this insight?" />
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <FormattedMessage
              id="insight.delete_insight_confirm"
              defaultMessage='"{name}" will be permanently removed, together with every report, config and risk score computed from it. This cannot be undone.'
              values={{ name: insightToDelete?.title || insightToDelete?.id }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="insight.delete_insight_note"
              defaultMessage="If one of its reports is a wave in a project, that wave is removed from the comparison. The assessment itself — its questions and collected responses — is kept."
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDeleteInsight}
            disabled={insightToDelete ? !!deletingInsight[insightToDelete.id] : false}
          >
            <FormattedMessage id="insight.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteInsight}
            disabled={insightToDelete ? !!deletingInsight[insightToDelete.id] : false}
            startIcon={
              insightToDelete && deletingInsight[insightToDelete.id]
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteIcon />
            }
          >
            <FormattedMessage id="insight.delete" defaultMessage="Delete" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Report Confirmation */}
      <Dialog
        open={!!reportToDelete}
        onClose={handleCancelDeleteReport}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <FormattedMessage id="insight.delete_report_title" defaultMessage="Delete this report?" />
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <FormattedMessage
              id="insight.delete_report_confirm"
              defaultMessage='"{name}" will be permanently deleted. This cannot be undone.'
              values={{ name: reportToDelete?.name || reportToDelete?.id }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="insight.delete_report_waves_note"
              defaultMessage="If this report is a wave in a project, that wave is removed from the comparison too. The project itself, its documents and its chat history are kept."
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDeleteReport}
            disabled={reportToDelete ? !!deletingReport[reportToDelete.id] : false}
          >
            <FormattedMessage id="insight.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteReport}
            disabled={reportToDelete ? !!deletingReport[reportToDelete.id] : false}
            startIcon={
              reportToDelete && deletingReport[reportToDelete.id]
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteIcon />
            }
          >
            <FormattedMessage id="insight.delete" defaultMessage="Delete" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Survey Selection Modal */}
      <Dialog
        open={surveyModalOpen}
        onClose={handleCloseSurveyModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <FormattedMessage id="insight.select_survey_to_generate" defaultMessage="Select Assessment to Generate Report" />
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Search and Summary */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder={intl.formatMessage({ id: "insight.search_surveys", defaultMessage: "Search assessments..." })}
              value={surveyModalSearch}
              onChange={(e) => handleModalSearch(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="insight.showing_surveys" defaultMessage="Showing {showing} of {total} assessments" values={{ showing: surveysForModal.length, total: surveyModalTotalItems }} />
            </Typography>
          </Box>

          {surveyModalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <List sx={{ py: 0, maxHeight: "400px", overflow: "auto" }}>
                {surveysForModal.map((config) => (
                  <ListItem key={config.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                      onClick={() => {
                        console.log("Selected config ID:", config.id);
                        console.log("Selected insight ID:", config.insightId);
                        setSelectedSurveyId(config.id);
                      }}
                      disabled={generating[config.id]}
                      selected={selectedSurveyId === config.id}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 2,
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "primary.50"
                        },
                        "&.Mui-selected": {
                          borderColor: "primary.main",
                          borderWidth: "2px",
                          bgcolor: "primary.50",
                          "&:hover": {
                            bgcolor: "primary.50"
                          }
                        }
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            {editingConfigId === config.id ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <TextField
                                  size="small"
                                  value={editingConfigName}
                                  onChange={(e) => setEditingConfigName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSaveConfigName(config.id);
                                    } else if (e.key === "Escape") {
                                      handleCancelEditConfig();
                                    }
                                  }}
                                  autoFocus
                                  disabled={renamingConfig[config.id]}
                                  sx={{ flex: 1, minWidth: 200 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleSaveConfigName(config.id)}
                                  disabled={renamingConfig[config.id]}
                                  color="primary"
                                >
                                  {renamingConfig[config.id] ? <CircularProgress size={16} /> : <CheckIcon />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEditConfig}
                                  disabled={renamingConfig[config.id]}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                                  {config.surveyTitle}
                                </Typography>
                                <Tooltip title={intl.formatMessage({ id: "insight.rename_config", defaultMessage: "Rename config" })}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartEditConfig(config.id, config.surveyTitle);
                                    }}
                                    disabled={renamingConfig[config.id]}
                                    sx={{
                                      padding: 0.25,
                                      "&:hover": {
                                        bgcolor: "action.hover"
                                      }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                              {config.insightTitle}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                            {editingConfigId !== config.id && (
                              <>
                                <Typography variant="body2" color="primary.main" sx={{ fontWeight: "medium" }}>
                                  {config.questionCount ?? 0}{" "}
                                  <FormattedMessage id="insight.configured_questions" defaultMessage="Questions" />
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "medium" }}>
                                  {Object.values(config.metadata?.num_topics ?? {}).reduce((a, b) => a + Number(b), 0)}{" "}
                                  <FormattedMessage id="insight.topics" defaultMessage="Topics" />
                                </Typography>
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    background: "linear-gradient(135deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.12) 100%)"
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    {config.insightCreated ? new Date(config.insightCreated).toLocaleDateString() : "N/A"}
                                  </Typography>
                                </Box>
                              </>
                            )}
                            <Chip
                              label={config.hasReport ? "HAS REPORT" : "NO REPORT"}
                              color={config.hasReport ? "success" : "warning"}
                              size="small"
                            />
                            {selectedSurveyId === config.id && (
                              <Tooltip title={intl.formatMessage({ id: "insight.configure_report", defaultMessage: "Configure report" })}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfigSurvey(config.id);
                                  }}
                                  disabled={configLoading}
                                  sx={{ color: "primary.main" }}
                                >
                                  {configLoading ? <CircularProgress size={16} /> : <SettingsIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {generating[config.insightId] && <CircularProgress size={16} />}
                          </Box>
                        </Box>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {surveysForModal.length === 0 && !surveyModalLoading && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {surveyModalSearch ? <FormattedMessage id="insight.no_surveys_match_search" defaultMessage="No assessments match your search." /> : <FormattedMessage id="insight.no_surveys_available" defaultMessage="No assessments available. Please create an assessment first." />}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2, flexDirection: "column", gap: 2 }}>
          <Box sx={{ width: "100%", display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={intl.formatMessage({ id: "insight.report_name", defaultMessage: "Report Name (optional)" })}
              placeholder={intl.formatMessage({ id: "insight.report_name_placeholder", defaultMessage: "Enter a name for this report" })}
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
            />
          </Box>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="insight.page_of" defaultMessage="Page {current} of {total}" values={{ current: surveyModalPage, total: surveyModalTotalPages }} />
              </Typography>
              {surveyModalTotalPages > 1 && (
                <Pagination
                  count={surveyModalTotalPages}
                  page={surveyModalPage}
                  onChange={handleModalPageChange}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={handleCloseSurveyModal}>
                <FormattedMessage id="insight.cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                variant="contained"
                onClick={handleDirectGeneration}
                disabled={!selectedSurveyId || generating[selectedSurveyId]}
              >
                <FormattedMessage id="insight.generate_report" defaultMessage="Generate Report" />
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Report Configuration Modal */}
      <Dialog
        open={configModalOpen}
        onClose={handleCloseConfigModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <FormattedMessage id="insight.configure_report_generation" defaultMessage="Configure Report Generation" />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <FormattedMessage id="insight.configure_settings_description" defaultMessage="Configure settings for each open text question" />
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, maxHeight: "70vh", overflowY: "auto" }}>
          {configLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* General Settings Section */}
              <Box sx={{ mb: 4, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                  <FormattedMessage id="insight.general_settings" defaultMessage="General Settings" />
                </Typography>

                {/* Language Selection */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel><FormattedMessage id="insight.language_label" defaultMessage="Language" /></InputLabel>
                  <Select
                    value={reportConfig.lang}
                    label={intl.formatMessage({ id: "insight.language_label", defaultMessage: "Language" })}
                    onChange={(e) => setReportConfig({ ...reportConfig, lang: e.target.value })}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="fr">Français</MenuItem>
                  </Select>
                </FormControl>

                {/* General Stop Words */}
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={intl.formatMessage({ id: "insight.general_stop_words_label", defaultMessage: "General Stop Words" })}
                  placeholder={intl.formatMessage({ id: "insight.stop_words_placeholder", defaultMessage: "Enter stop words separated by commas (e.g., the, and, or)" })}
                  value={reportConfig.generalStopWords.join(", ")}
                  onChange={(e) => setReportConfig({
                    ...reportConfig,
                    generalStopWords: e.target.value.split(",").map(word => word.trim()).filter(word => word)
                  })}
                  helperText={intl.formatMessage({ id: "insight.stop_words_helper", defaultMessage: "These stop words will apply to all questions" })}
                />
              </Box>

              {/* View Toggle */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <ToggleButtonGroup
                  value={configViewTab}
                  exclusive
                  onChange={(e, newValue) => { if (newValue !== null) setConfigViewTab(newValue); }}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      px: 3,
                      fontWeight: 600,
                    },
                    "& .Mui-selected": {
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                    },
                  }}
                >
                  <ToggleButton value="demog">
                    <FormattedMessage id="insight.tab_demog" defaultMessage="Demog" />
                  </ToggleButton>
                  <ToggleButton value="pillars">
                    <FormattedMessage id="insight.tab_pillars" defaultMessage="Pillars" />
                  </ToggleButton>
                  <ToggleButton value="topics">
                    <FormattedMessage id="insight.tab_topics" defaultMessage="Topics" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* ============ DEMOG TAB ============ */}
              {configViewTab === "demog" && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <FormattedMessage id="insight.demog_questions" defaultMessage="Demographic Questions" />
                    <Chip
                      size="small"
                      label={Object.keys(reportConfig.demog_questions || {}).length}
                      color="primary"
                      variant="outlined"
                    />
                  </Typography>
                  {Object.keys(reportConfig.demog_questions || {}).length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {Object.entries(reportConfig.demog_questions).map(([questionKey, demog]) => (
                        <Paper
                          key={questionKey}
                          variant="outlined"
                          sx={{
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!demog.is_selected}
                                onChange={(e) => handleDemogSelectionChange(questionKey, e.target.checked)}
                                color="primary"
                              />
                            }
                            label=""
                            sx={{ m: 0, flexShrink: 0 }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label={intl.formatMessage({ id: "insight.demog_display_title", defaultMessage: "Display Title" })}
                            placeholder={questionKey}
                            value={demog.title ?? questionKey}
                            onChange={(e) => handleDemogTitleChange(questionKey, e.target.value)}
                            helperText={
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                {questionKey}
                              </Typography>
                            }
                            sx={{ flex: 1, minWidth: 200 }}
                          />
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 3, bgcolor: "grey.50", borderRadius: 1 }}>
                      <People sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="insight.no_demog_questions" defaultMessage="No demographic questions found in this configuration" />
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
                        <FormattedMessage id="insight.demog_from_metadata" defaultMessage="Demographic questions are populated from the assessment metadata when available." />
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* ============ PILLARS TAB ============ */}
              {configViewTab === "pillars" && (
                <Box>
                  {/* Likert Questions Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                      <FormattedMessage id="insight.likert_questions" defaultMessage="Likert Questions" />
                      <Chip size="small" label={getLikertQuestions().length} color="primary" variant="outlined" />
                    </Typography>
                    {getLikertQuestions().length > 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {getLikertQuestions().map((q) => (
                          <Paper
                            key={q.id}
                            variant="outlined"
                            sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
                          >
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                              {q.title}
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel sx={{ color: "#000000", "&.Mui-focused": { color: "#1976d2" } }}>
                                <FormattedMessage id="insight.pillar" defaultMessage="Pillar" />
                              </InputLabel>
                              <Select
                                value={q.pillar || ""}
                                label={intl.formatMessage({ id: "insight.pillar", defaultMessage: "Pillar" })}
                                onChange={(e) => handleLikertPillarChange(q.title, e.target.value)}
                                sx={{
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: q.pillar ? "rgba(0, 0, 0, 0.23)" : "#d32f2f",
                                    borderWidth: "2px",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: q.pillar ? "rgba(0, 0, 0, 0.5)" : "#d32f2f",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2",
                                  },
                                }}
                              >
                                {PILLAR_OPTIONS.map((pillar) => (
                                  <MenuItem key={pillar} value={pillar}>
                                    {pillar}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 3, bgcolor: "grey.50", borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <FormattedMessage id="insight.no_likert_questions" defaultMessage="No likert questions found in this configuration" />
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Open Text Questions Section */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                      <FormattedMessage id="insight.open_text_questions_pillars" defaultMessage="Open Text Questions" />
                      <Chip size="small" label={getOpenTextQuestionsForPillars().length} color="secondary" variant="outlined" />
                    </Typography>
                    {getOpenTextQuestionsForPillars().length > 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {getOpenTextQuestionsForPillars().map((q) => (
                          <Paper
                            key={q.id}
                            variant="outlined"
                            sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
                          >
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                              {q.title}
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel sx={{ color: "#000000", "&.Mui-focused": { color: "#1976d2" } }}>
                                <FormattedMessage id="insight.pillar" defaultMessage="Pillar" />
                              </InputLabel>
                              <Select
                                value={q.pillar || ""}
                                label={intl.formatMessage({ id: "insight.pillar", defaultMessage: "Pillar" })}
                                onChange={(e) => handleOpenTextPillarChange(q.title, e.target.value)}
                                sx={{
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: q.pillar ? "rgba(0, 0, 0, 0.23)" : "#d32f2f",
                                    borderWidth: "2px",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: q.pillar ? "rgba(0, 0, 0, 0.5)" : "#d32f2f",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2",
                                  },
                                }}
                              >
                                {PILLAR_OPTIONS.map((pillar) => (
                                  <MenuItem key={pillar} value={pillar}>
                                    {pillar}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 3, bgcolor: "grey.50", borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <FormattedMessage id="insight.no_open_text_questions_pillars" defaultMessage="No open text questions found in this configuration" />
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* ============ TOPICS TAB ============ */}
              {configViewTab === "topics" && (
                <>
                  {getOpenTextQuestions().length > 0 ? (
                    <Box>
                      <Divider sx={{ mb: 3 }} />

                      {(() => {
                        const openTextQuestions = getOpenTextQuestions();
                        const currentQuestion = openTextQuestions[currentQuestionIndex];
                        const questionTitle = currentQuestion?.title;
                        const questionConfig = reportConfig.questionConfigs[questionTitle] || {};

                        return (
                          <>
                            {/* Question Header with Navigation */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                              <IconButton
                                onClick={handlePreviousQuestion}
                                disabled={currentQuestionIndex === 0}
                                sx={{ mr: 2 }}
                              >
                                <ArrowBack />
                              </IconButton>

                              <Box sx={{ flex: 1, textAlign: "center" }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  <FormattedMessage id="insight.question_of" defaultMessage="Question {current} of {total}" values={{ current: currentQuestionIndex + 1, total: openTextQuestions.length }} />
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                  {questionTitle || intl.formatMessage({ id: "insight.untitled_question", defaultMessage: "Untitled Question" })}
                                </Typography>
                              </Box>

                              <IconButton
                                onClick={handleNextQuestion}
                                disabled={currentQuestionIndex === openTextQuestions.length - 1}
                                sx={{ ml: 2 }}
                              >
                                <ArrowForward />
                              </IconButton>
                            </Box>

                            {/* Question Configuration Fields */}
                            <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                              {/* Number of Topics */}
                              <TextField
                                fullWidth
                                type="number"
                                label={intl.formatMessage({ id: "insight.number_of_topics", defaultMessage: "Number of Topics" })}
                                value={questionConfig.numTopics}
                                onChange={(e) => handleNumTopicsChange(questionTitle, e.target.value)}
                                inputProps={{ min: 1, max: 50 }}
                                sx={{ mb: 2 }}
                                helperText={intl.formatMessage({ id: "insight.default_3", defaultMessage: "Default: 3" })}
                              />

                              {/* Number of Keywords */}
                              <TextField
                                fullWidth
                                type="number"
                                label={intl.formatMessage({ id: "insight.number_of_keywords", defaultMessage: "Number of Keywords" })}
                                value={questionConfig.numKeywords}
                                onChange={(e) => updateQuestionConfig(questionTitle, "numKeywords", parseInt(e.target.value) || 7)}
                                inputProps={{ min: 1, max: 100 }}
                                sx={{ mb: 2 }}
                                helperText={intl.formatMessage({ id: "insight.default_7", defaultMessage: "Default: 7" })}
                              />

                              {/* Stop Words */}
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label={intl.formatMessage({ id: "insight.stop_words_specific", defaultMessage: "Stop Words (specific to this question)" })}
                                placeholder={intl.formatMessage({ id: "insight.stop_words_placeholder_specific", defaultMessage: "Enter stop words separated by commas" })}
                                value={questionConfig.stopWords.join(", ")}
                                onChange={(e) => updateQuestionConfig(
                                  questionTitle,
                                  "stopWords",
                                  e.target.value.split(",").map(word => word.trim()).filter(word => word)
                                )}
                                sx={{ mb: 3 }}
                                helperText={intl.formatMessage({ id: "insight.stop_words_helper_specific", defaultMessage: "These stop words are specific to this question only" })}
                              />

                              {/* Topic Titles */}
                              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
                                <FormattedMessage id="insight.topic_titles" defaultMessage="Topic Titles ({count} topics)" values={{ count: questionConfig.numTopics }} />
                              </Typography>
                              {questionConfig.topicTitles.map((title, index) => (
                                <TextField
                                  key={index}
                                  fullWidth
                                  label={intl.formatMessage({ id: "insight.topic_title", defaultMessage: "Topic {number} Title" }, { number: index + 1 })}
                                  placeholder={intl.formatMessage({ id: "insight.topic_title_placeholder", defaultMessage: "Enter title for topic {number}" }, { number: index + 1 })}
                                  value={title}
                                  onChange={(e) => {
                                    const newTitles = [...questionConfig.topicTitles];
                                    newTitles[index] = e.target.value;
                                    updateQuestionConfig(questionTitle, "topicTitles", newTitles);
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              ))}

                              {/* Topic Cards - Display existing topic analysis if available */}
                              {reportConfig.topic_cards?.[questionTitle] && reportConfig.topic_cards[questionTitle].length > 0 && (
                                <Accordion defaultExpanded={false} sx={{ mt: 3, bgcolor: "rgb(129 146 151)", borderRadius: 1 }}>
                                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "white" }}>
                                      <FormattedMessage id="insight.auto_config_results" defaultMessage="Auto config results" />
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${reportConfig.topic_cards[questionTitle].length} topics`}
                                      sx={{ bgcolor: "rgba(37 23 167 / 0.48)", color: "white", fontWeight: 500 }}
                                    />
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      {reportConfig.topic_cards[questionTitle].map((topicCard, cardIndex) => (
                                        <Paper
                                          key={cardIndex}
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            borderColor: topicCard.should_review ? "error.main" : "divider",
                                            borderWidth: topicCard.should_review ? 2 : 1,
                                            bgcolor: topicCard.should_review ? "error.50" : "grey.50",
                                            position: "relative"
                                          }}
                                        >
                                          {/* Header with label and review status */}
                                          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
                                            <Box sx={{ flex: 1 }}>
                                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
                                                {topicCard.label || `Topic ${cardIndex + 1}`}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {topicCard.n_responses} responses • Confidence: {((topicCard.confidence || 0) * 100).toFixed(0)}%
                                              </Typography>
                                            </Box>
                                            {topicCard.should_review && (
                                              <Chip
                                                size="small"
                                                label="Needs Review"
                                                color="error"
                                                sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                                              />
                                            )}
                                          </Box>

                                          {/* Description */}
                                          {topicCard.description && (
                                            <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary", fontStyle: "italic" }}>
                                              {topicCard.description}
                                            </Typography>
                                          )}

                                          {/* Keywords */}
                                          {topicCard.keywords && topicCard.keywords.length > 0 && (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                              {topicCard.keywords.slice(0, 8).map((kw, kwIdx) => (
                                                <Chip
                                                  key={kwIdx}
                                                  size="small"
                                                  label={`${kw.term} (${(kw.weight * 100).toFixed(1)}%)`}
                                                  sx={{
                                                    bgcolor: "primary.100",
                                                    color: "primary.dark",
                                                    fontSize: "0.7rem",
                                                    height: 24
                                                  }}
                                                />
                                              ))}
                                            </Box>
                                          )}
                                        </Paper>
                                      ))}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              )}
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="insight.no_open_text_questions" defaultMessage="No open text questions found in this assessment" />
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", px: 3, pb: 2, pt: 2, gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            label={intl.formatMessage({ id: "insight.report_name", defaultMessage: "Report Name (optional)" })}
            placeholder={intl.formatMessage({ id: "insight.report_name_placeholder", defaultMessage: "Enter a name for this report" })}
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={handleCloseConfigModal} disabled={configLoading}>
              <FormattedMessage id="insight.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSaveConfiguration(false)}
              disabled={configLoading || !selectedSurveyId}
            >
              <FormattedMessage id="insight.save_configuration" defaultMessage="Save Configuration" />
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAndGenerate}
              disabled={configLoading || !selectedSurveyId}
            >
              <FormattedMessage id="insight.save_and_generate" defaultMessage="Save & Generate Report" />
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <PillarTaggingReviewDialog
        open={Boolean(reviewTarget)}
        insightId={reviewTarget?.insightId}
        configId={reviewTarget?.configId}
        onClose={() => setReviewTarget(null)}
        onConfirmed={() => {
          // Back to running: the resume advances it from `topics`, and the
          // existing poll picks the new steps up from here.
          const id = reviewTarget?.insightId;
          if (id) {
            setGeneratingInsights((prev) => (
              prev[id]
                ? {
                    ...prev,
                    [id]: {
                      ...prev[id], state: "running", step: 4, configId: null,
                      confirmedAt: Date.now(),
                    },
                  }
                : prev
            ));
          }
        }}
      />

      {/* Generate Config Modal */}
      <Dialog
        open={generateConfigModalOpen}
        onClose={handleCloseGenerateConfigModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <FormattedMessage id="insight.select_survey_for_full_report" defaultMessage="Select Assessment to Generate Full Report" />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <FormattedMessage id="insight.config_description_full" defaultMessage="Choose an assessment to run the whole pipeline in one job: pillars, topics, report and the risk data behind the Risk tab" />
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* The taxonomy applies to whichever input is chosen below, so it sits
              above both. Optional and collapsed: empty means today's behaviour. */}
          <PillarsDefEditor
            rows={pillarRows}
            onChange={handlePillarRowsChange}
            disabled={jsonImporting || !!triggeringAutoconfig[selectedConfigSurveyId]}
            toolbar={
              // Not saved as a draft: a draft is someone's in-progress edit,
              // and a prefill is just where the rows start.
              <ProjectPillarsPicker
                onLoad={setPillarRows}
                disabled={jsonImporting || !!triggeringAutoconfig[selectedConfigSurveyId]}
              />
            }
          />

          {/* Import a raw_input JSON document instead of picking a collected
              survey: this path creates its own insight, so it has nothing to do
              with the selection below. */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                border: "1px dashed",
                borderColor: jsonError ? "error.main" : "divider",
                borderRadius: 1,
                bgcolor: "grey.50",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                  <FormattedMessage
                    id="insight.import_json_title"
                    defaultMessage="Or import a raw_input JSON file"
                  />
                </Typography>
                <input
                  ref={jsonInputRef}
                  type="file"
                  accept="application/json,.json"
                  hidden
                  onChange={handleJsonFileSelected}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={jsonChecking ? <CircularProgress size={14} /> : <UploadFileIcon />}
                  onClick={() => jsonInputRef.current?.click()}
                  disabled={jsonChecking || jsonImporting}
                >
                  <FormattedMessage id="insight.choose_json_file" defaultMessage="Choose file" />
                </Button>
                {jsonFile && (
                  <Button size="small" onClick={resetJsonImport} disabled={jsonImporting}>
                    <FormattedMessage id="insight.clear" defaultMessage="Clear" />
                  </Button>
                )}
              </Box>
              {/* The uploader states the language; nothing here guesses it.
                  It is sent with the upload and stored as the insight's
                  `language`, which is what picks the tokenizer and stop-word
                  list — so a Spanish file stops being stripped with English
                  stop words. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <FormattedMessage
                    id="insight.import_json_language"
                    defaultMessage="Language of the responses"
                  />
                </Typography>
                <ToggleButtonGroup
                  value={jsonLanguage}
                  exclusive
                  onChange={(e, newValue) => { if (newValue !== null) setJsonLanguage(newValue); }}
                  size="small"
                  disabled={jsonChecking || jsonImporting}
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      px: 2,
                      fontWeight: 600,
                    },
                    "& .Mui-selected": {
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                    },
                  }}
                >
                  <ToggleButton value="en">English</ToggleButton>
                  <ToggleButton value="es">Español</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                <FormattedMessage
                  id="insight.import_json_language_hint"
                  defaultMessage="Set this to the language the answers are written in — it selects the stop words and topic model used for the report."
                />
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                <FormattedMessage
                  id="insight.import_json_hint"
                  defaultMessage="The file is checked before anything is created. Older exports without likert_groups / likert_pillars are converted automatically."
                />
              </Typography>

              {jsonFile && (
                <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                  {jsonFile.name}
                </Typography>
              )}

              {jsonError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>{jsonError}</Alert>
              )}

              {jsonCheck?.valid && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                    <Chip size="small" label={intl.formatMessage(
                      { id: "insight.json_respondents", defaultMessage: "{count} respondents" },
                      { count: jsonCheck.summary?.respondents ?? 0 }
                    )} />
                    <Chip size="small" label={intl.formatMessage(
                      { id: "insight.json_open_text", defaultMessage: "{count} open text" },
                      { count: jsonCheck.summary?.open_text_questions ?? 0 }
                    )} />
                    <Chip size="small" label={intl.formatMessage(
                      { id: "insight.json_likert", defaultMessage: "{count} likert" },
                      { count: jsonCheck.summary?.likert_statements ?? 0 }
                    )} />
                    <Chip size="small" label={intl.formatMessage(
                      { id: "insight.json_demographics", defaultMessage: "{count} demographics" },
                      { count: jsonCheck.summary?.demographics ?? 0 }
                    )} />
                    {(jsonCheck.summary?.pillars || []).map((pillar) => (
                      <Chip key={pillar} size="small" variant="outlined" label={pillar} />
                    ))}
                  </Box>

                  {(jsonCheck.will_infer || []).length > 0 && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        <FormattedMessage
                          id="insight.json_will_infer"
                          defaultMessage="This file is an older export. It will be converted:"
                        />
                      </Typography>
                      <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {jsonCheck.will_infer.map((note) => (
                          <li key={note}><Typography variant="body2">{note}</Typography></li>
                        ))}
                      </Box>
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    startIcon={jsonImporting ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                    onClick={handleImportJson}
                    disabled={jsonImporting}
                  >
                    <FormattedMessage
                      id="insight.import_json_generate"
                      defaultMessage="Import & Generate Full Report"
                    />
                  </Button>
                </Box>
              )}
            </Box>

          {/* Search and Summary */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder={intl.formatMessage({ id: "insight.search_surveys", defaultMessage: "Search assessments..." })}
              value={generateConfigSearch}
              onChange={(e) => handleGenerateConfigSearch(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="insight.showing_surveys" defaultMessage="Showing {showing} of {total} assessments" values={{ showing: generateConfigSurveys.length, total: generateConfigTotalItems }} />
            </Typography>
          </Box>

          {generateConfigLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <List sx={{ py: 0, maxHeight: "400px", overflow: "auto" }}>
                {generateConfigSurveys.map((insight) => (
                  <ListItem key={insight.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                      onClick={() => {
                        console.log("Selected insight for config:", insight.id, "Survey ID:", insight.surveyId);
                        setSelectedConfigSurveyId(insight.id);
                      }}
                      disabled={triggeringAutoconfig[insight.id]}
                      selected={selectedConfigSurveyId === insight.id}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 2,
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "primary.50"
                        },
                        "&.Mui-selected": {
                          borderColor: "primary.main",
                          borderWidth: "2px",
                          bgcolor: "primary.50",
                          "&:hover": {
                            bgcolor: "primary.50"
                          }
                        }
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                          {editingInsightId === insight.id ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                              <TextField
                                size="small"
                                value={editingInsightName}
                                onChange={(e) => setEditingInsightName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleSaveInsightName(insight.id);
                                  } else if (e.key === "Escape") {
                                    handleCancelEditInsight();
                                  }
                                }}
                                autoFocus
                                disabled={renamingInsight[insight.id]}
                                sx={{ flex: 1, minWidth: 200 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleSaveInsightName(insight.id)}
                                disabled={renamingInsight[insight.id]}
                                color="primary"
                              >
                                {renamingInsight[insight.id] ? <CircularProgress size={16} /> : <CheckIcon />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEditInsight}
                                disabled={renamingInsight[insight.id]}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                                {insight.title}
                              </Typography>
                              <Tooltip title={intl.formatMessage({ id: "insight.rename_insight", defaultMessage: "Rename insight" })}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEditInsight(insight.id, insight.title);
                                  }}
                                  disabled={renamingInsight[insight.id]}
                                  sx={{
                                    padding: 0.25,
                                    "&:hover": {
                                      bgcolor: "action.hover"
                                    }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                            {editingInsightId !== insight.id && (
                              <>
                                <Typography variant="body2" color="primary.main" sx={{ fontWeight: "medium" }}>
                                  {insight.completelyResponded ?? 0}{" "}
                                  <FormattedMessage id="insight.responses" defaultMessage="Responses" />
                                </Typography>
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    background: "linear-gradient(135deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.12) 100%)"
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    {insight.created ? new Date(insight.created).toLocaleDateString() : "N/A"}
                                  </Typography>
                                </Box>
                              </>
                            )}
                            <Chip
                              label={insight.hasReports ? "HAS REPORTS" : "NO REPORTS"}
                              color={insight.hasReports ? "success" : "warning"}
                              size="small"
                            />
                            <Tooltip title={intl.formatMessage({ id: "insight.delete_insight", defaultMessage: "Remove insight and its data" })}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAskDeleteInsight(insight);
                                }}
                                disabled={!!deletingInsight[insight.id] || !!renamingInsight[insight.id]}
                                aria-label={intl.formatMessage({ id: "insight.delete_insight", defaultMessage: "Remove insight and its data" })}
                                sx={{ padding: 0.25 }}
                              >
                                {deletingInsight[insight.id]
                                  ? <CircularProgress size={16} />
                                  : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            {triggeringAutoconfig[insight.surveyId] && <CircularProgress size={16} />}
                          </Box>
                        </Box>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {generateConfigSurveys.length === 0 && !generateConfigLoading && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {generateConfigSearch ? <FormattedMessage id="insight.no_surveys_match_search" defaultMessage="No assessments match your search." /> : <FormattedMessage id="insight.no_surveys_available" defaultMessage="No assessments available. Please create an assessment first." />}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2, flexDirection: "column", gap: 2 }}>
          <Box sx={{ width: "100%", display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={intl.formatMessage({ id: "insight.config_name", defaultMessage: "Config/Report Name (optional)" })}
              placeholder={intl.formatMessage({ id: "insight.config_name_placeholder", defaultMessage: "Enter a name for this config or report" })}
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
            />
          </Box>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="insight.page_of" defaultMessage="Page {current} of {total}" values={{ current: generateConfigPage, total: generateConfigTotalPages }} />
              </Typography>
              {generateConfigTotalPages > 1 && (
                <Pagination
                  count={generateConfigTotalPages}
                  page={generateConfigPage}
                  onChange={handleGenerateConfigPageChange}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={handleCloseGenerateConfigModal}>
                <FormattedMessage id="insight.cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                variant="contained"
                onClick={handleBuildInsight}
                disabled={!selectedConfigSurveyId || triggeringAutoconfig[selectedConfigSurveyId]}
                startIcon={<Refresh />}
              >
                <FormattedMessage id="insight.generate_full_report" defaultMessage="Generate Full Report" />
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
